import BigNumber from 'bignumber.js';
import { createSelector } from 'reselect';

import {
  BasketAnalyticsGreeksTableRowData,
  BasketAnalyticsGreeksTotalData,
  BasketAnalyticsPNLTableRowData,
} from 'components/basketAnalytics/BasketAnalyticsTabSection/BasketAnalyticsTabSection.types';
import {
  basketAnalyzeComputeGreek,
  basketAnalyzeFormatGreekValue,
  basketAnalyzeGetCheckDateUTC,
} from 'components/basketOrders/BasketAnalyze/BasketAnalyze.helper';
import { GREEK_PRECISION, ORDER_SIDE, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { GREEK_TYPES, ORDER_TYPE } from 'constants/enums';
import { TIME_FORMATS } from 'constants/timeformats';
import { round_by_contract_size as roundByContractSize } from 'helpers/assetUtils';
import { dateFormat, dayjs, toUTC } from 'helpers/day';
import {
  divide,
  filter,
  flip,
  groupBy,
  includes,
  isEmpty,
  map,
  mapObjIndexed,
  max,
  min,
  multiply,
  partition,
  pipe,
  pluck,
  prop,
  reduce,
  sort,
  subtract,
  sum,
  uniq,
} from 'helpers/ramda';
import {
  cropAfterDecimals,
  isFutures,
  numberCommaSeparator,
  numberPrettified,
} from 'helpers/utils';
import { NumberSign, PNLData } from 'types/basketAnalyze';
import { ContractType } from 'types/IProducts';
import IState from 'types/Istore';
import { PositionAnalyticsState } from 'types/positionAnalyse';

import {
  deltaPriceSelectorById,
  gammaPriceSelectorById,
  markPriceSelectorById,
  oiSelectorById,
  thetaPriceSelectorById,
  vegaPriceSelectorById,
} from './priceSelectors';
import { selectAllOptionsChainContractsTickers } from './tickerSelectors';
import { productsSelector } from './tradeSelectors';

// TODO: Need to move common functionalities for both position and basket analytics in common file.

const isInfinity = value => value === Infinity || value === -Infinity;

const formatValue = pipe(Number, num => cropAfterDecimals(num, 2), numberPrettified);

const getNumberSign = number => {
  if (number === 0 || !Number.isFinite(number)) return NumberSign.DEFAULT;
  return number > 0 ? NumberSign.POSITIVE : NumberSign.NEGATIVE;
};

const positionAnalyseSelector = (state: IState) => state.positionAnalyse;

const positionAnalysePnlDataSelector = createSelector(
  [positionAnalyseSelector],
  (positionAnalyse: PositionAnalyticsState) => {
    const {
      max_loss: maxLoss,
      max_profit: maxProfit,
      break_even_point: breakEvenPoints,
    } = positionAnalyse?.positionAnalytics?.data ?? {};

    const maxProfitSign = getNumberSign(Number(maxProfit));
    const maxLossSign = getNumberSign(Number(maxLoss));

    // TODO: remove this uniq once BE sends uniqe value for breakEvenPoints on case
    // of smaller fraction digits after 2 digits
    // example current response : ['61857.79760602002109012102489', '61857.79760602002171044378736']
    const formattedBreakEvenPoints =
      Array.isArray(breakEvenPoints) && !isEmpty(breakEvenPoints)
        ? pipe(
            map(point => cropAfterDecimals(Number(point), 2)),
            uniq
          )(breakEvenPoints)
        : ['NA'];

    const isInfinityProfit = isInfinity(Number(maxProfit));
    const isInfinityLoss = isInfinity(Number(maxLoss));

    const formattedMaxProfit = isInfinityProfit ? 'Unlimited' : formatValue(maxProfit);

    const formattedMaxLoss = isInfinityLoss ? 'Unlimited' : formatValue(maxLoss);

    const rewardByRiskRawValue = Math.abs(Number(maxProfit)) / Math.abs(Number(maxLoss));

    const isRewardByRiskRawValueInfinity = isInfinity(rewardByRiskRawValue);

    const rewardByRisk =
      isInfinityProfit || isInfinityLoss || isRewardByRiskRawValueInfinity
        ? 'NA'
        : numberPrettified(rewardByRiskRawValue);

    return {
      formattedMaxProfit,
      formattedMaxLoss,
      formattedBreakEvenPoints,
      rewardByRisk,
      maxProfitSign,
      isProfitInfinite: isInfinityProfit,
      maxLossSign,
      isLossInfinite: isInfinityLoss,
    };
  }
);

const positionAnalyseOrdersSelector = createSelector(
  [positionAnalyseSelector],
  prop('orders')
);

const positionAnalyseActiveOrders = createSelector(
  [positionAnalyseOrdersSelector, (_, activeAsset) => activeAsset],
  (orders, activeAsset) => {
    const ordersOfCurrentActiveAsset = Object.values(orders).filter(
      order => order?.asset === activeAsset && order.side
    );
    return ordersOfCurrentActiveAsset;
  }
);

const positionAnalyseTargetDatesSelector = createSelector(
  [
    (state, activeAsset) => positionAnalyseActiveOrders(state, activeAsset),
    productsSelector,
    positionAnalyseSelector,
  ],
  (ordersOfCurrentActiveAsset, products, positionAnalyse: PositionAnalyticsState) => {
    const { selectedRows = [], selectedTargetDate } = positionAnalyse?.pnlTab ?? {};
    // since selected rows useEffect triggers on initial mount
    // else this will cause two calls on api on initial mount triggers with
    // current date and the current date + max date, issue fix for mobile
    if (!selectedRows.length && !selectedTargetDate) return [];

    let currentTimestamp;

    const now = dayjs().utc();
    const noonTodayUTC = now.startOf('day').add(12, 'hour');
    // Check if current time is past 12 PM UTC. If so, use the next day.
    if (now.isAfter(noonTodayUTC)) {
      currentTimestamp = now.add(1, 'day').startOf('day').add(12, 'hour').valueOf();
    } else {
      currentTimestamp = noonTodayUTC.valueOf();
    }

    const times = filter(
      order =>
        includes(order.product_id, selectedRows) && !isFutures(order.contract_type),
      ordersOfCurrentActiveAsset
    )?.map(order => {
      const selectedProduct = products[order.product_id];
      const settlementTimestamp = selectedProduct?.settlement_time
        ? new Date(selectedProduct.settlement_time).getTime()
        : new Date().getTime();
      return settlementTimestamp;
    });

    const sortedUniqueTimestamps = sort(
      (a, b) => a - b,
      uniq([currentTimestamp, ...times])
    );
    const minTimestamp = sortedUniqueTimestamps[0];
    const maxTimestamp = sortedUniqueTimestamps[sortedUniqueTimestamps.length - 1];

    const dailyTimestamps: number[] = [];
    let currentDay = dayjs(minTimestamp).utc().startOf('day').add(12, 'hour');

    while (currentDay.valueOf() <= maxTimestamp) {
      dailyTimestamps.push(currentDay.valueOf());
      currentDay = currentDay.add(1, 'day').startOf('day').add(12, 'hour');
    }

    dailyTimestamps[0] = currentTimestamp;
    return dailyTimestamps;
  }
);

const positionAnalyseTargetDatesIndexMapSelector = createSelector(
  [positionAnalyseTargetDatesSelector],
  targetDates => {
    return targetDates.reduce((indexMap, time, index) => {
      // eslint-disable-next-line no-param-reassign
      indexMap[index] = time;
      return indexMap;
    }, {});
  }
);

const positionAnalyseGreeksAndPnLDataSelector = createSelector(
  [
    (state, activeAsset) => positionAnalyseActiveOrders(state, activeAsset),
    productsSelector,
    positionAnalyseSelector,
  ],
  (ordersOfCurrentActiveAsset, products, positionAnalyse) => {
    const { pnlTab, positionAnalytics } = positionAnalyse ?? {};
    const { result_pnls: resultPnls = [] } = positionAnalytics?.data ?? {};
    const { selectedTargetPrice } = pnlTab;

    return ordersOfCurrentActiveAsset.reduce<
      [
        BasketAnalyticsGreeksTableRowData[],
        BasketAnalyticsGreeksTotalData,
        BasketAnalyticsPNLTableRowData[]
      ]
    >(
      ([greeksAcc, greeksTotalAcc, pnlAcc], order, index, ordersArray) => {
        const {
          product_id: orderProductId,
          size,
          side,
          symbol,
          asset: orderAsset,
        } = order;
        const selectedProduct = products?.[orderProductId];
        const rawSize = Number(size ?? 0) * Number(selectedProduct?.contract_value ?? 0);
        const isContractTypeFutures = isFutures(order.contract_type);
        const isMarketOrderType = order.order_type === ORDER_TYPE.MARKET;
        const entryPrice = isMarketOrderType
          ? Number(
              cropAfterDecimals(
                markPriceSelectorById(order.product_id),
                selectedProduct?.settling_asset?.minimum_precision
              )
            )
          : Number(order.limit_price);
        const selectedResultPnl = resultPnls.find(
          resultPnl =>
            parseInt(resultPnl.spot_price, 10) === Math.floor(selectedTargetPrice)
        );

        const estPrice = Number(
          selectedResultPnl?.individual_pnls?.[orderProductId]?.exit_price ?? 0
        );

        let targetPnl;
        // for short/sell
        if (side === ORDER_SIDE.SELL) {
          targetPnl =
            -1 *
            Number(size ?? 0) *
            Number(selectedProduct?.contract_value) *
            (Number(estPrice) - entryPrice);
        }
        // for long/buy
        if (side === ORDER_SIDE.BUY) {
          targetPnl =
            1 *
            Number(size ?? 0) *
            Number(selectedProduct?.contract_value) *
            (Number(estPrice) - entryPrice);
        }

        let delta;
        if (isContractTypeFutures) {
          const adjustedSignBasedDelta =
            order.side === ORDER_SIDE.BUY
              ? Number(Number(rawSize) * 1.0)
              : Number(Number(rawSize) * -1.0);
          delta = basketAnalyzeFormatGreekValue({
            greekValue: adjustedSignBasedDelta,
            precision: GREEK_PRECISION.DELTA,
          });
        } else {
          delta = basketAnalyzeComputeGreek({
            size: Number(rawSize ?? 0),
            selector: deltaPriceSelectorById,
            productId: orderProductId,
            precision: GREEK_PRECISION.DELTA,
            contractType: selectedProduct?.contract_type,
            side,
            greekType: GREEK_TYPES.DELTA,
          });
        }

        const gamma = basketAnalyzeComputeGreek({
          size: Number(rawSize ?? 0),
          selector: gammaPriceSelectorById,
          productId: orderProductId,
          precision: GREEK_PRECISION.GAMMA,
          contractType: selectedProduct?.contract_type,
          side,
          greekType: GREEK_TYPES.GAMMA,
        });

        const theta = basketAnalyzeComputeGreek({
          size: Number(rawSize ?? 0),
          selector: thetaPriceSelectorById,
          productId: orderProductId,
          precision: GREEK_PRECISION.THETA,
          contractType: selectedProduct?.contract_type,
          side,
          greekType: GREEK_TYPES.THETA,
        });

        const vega = basketAnalyzeComputeGreek({
          size: Number(rawSize ?? 0),
          selector: vegaPriceSelectorById,
          productId: orderProductId,
          precision: GREEK_PRECISION.VEGA,
          contractType: selectedProduct?.contract_type,
          side,
          greekType: GREEK_TYPES.VEGA,
        });

        const greeksTableRowData = {
          side,
          symbol,
          delta,
          gamma,
          theta,
          vega,
        };

        const roundedSize = Number(
          roundByContractSize(rawSize, selectedProduct?.contract_value)
        );

        const pnlTableRowData = {
          product_id: orderProductId,
          side,
          symbol,
          size: roundedSize,
          asset: orderAsset,
          est_price: Number(cropAfterDecimals(estPrice, 2)),
          entry_price: Number(cropAfterDecimals(entryPrice, 2)),
          target_pnl: Number(cropAfterDecimals(targetPnl, 2)),
        };

        greeksAcc.push(greeksTableRowData);
        pnlAcc.push(pnlTableRowData);

        // added bignumber since for case like -1.68 + -0.16 => -1.8399999999999999
        // to fix the floating point number precision used this so it will return -1.84
        let updatedGreeksTotalAcc = {
          deltaTotal: new BigNumber(greeksTotalAcc.deltaTotal)
            .plus(new BigNumber(delta))
            .toNumber(),
          gammaTotal: new BigNumber(greeksTotalAcc.gammaTotal)
            .plus(new BigNumber(gamma))
            .toNumber(),
          thetaTotal: new BigNumber(greeksTotalAcc.thetaTotal)
            .plus(new BigNumber(theta))
            .toNumber(),
          vegaTotal: new BigNumber(greeksTotalAcc.vegaTotal)
            .plus(new BigNumber(vega))
            .toNumber(),
        };

        // Apply scientificToDecimalNotation on the last iteration
        if (index === ordersArray.length - 1) {
          updatedGreeksTotalAcc = {
            deltaTotal: basketAnalyzeFormatGreekValue({
              greekValue: updatedGreeksTotalAcc.deltaTotal,
              precision: GREEK_PRECISION.DELTA,
            }),
            gammaTotal: basketAnalyzeFormatGreekValue({
              greekValue: updatedGreeksTotalAcc.gammaTotal,
              precision: GREEK_PRECISION.GAMMA,
            }),
            thetaTotal: basketAnalyzeFormatGreekValue({
              greekValue: updatedGreeksTotalAcc.thetaTotal,
              precision: GREEK_PRECISION.THETA,
            }),
            vegaTotal: basketAnalyzeFormatGreekValue({
              greekValue: updatedGreeksTotalAcc.vegaTotal,
              precision: GREEK_PRECISION.VEGA,
            }),
          };
        }

        return [greeksAcc, updatedGreeksTotalAcc, pnlAcc];
      },
      [[], { deltaTotal: 0, gammaTotal: 0, thetaTotal: 0, vegaTotal: 0 }, []]
    );
  }
);

const positionAnalyseResultSpotPricesSelector = createSelector(
  [positionAnalyseSelector],
  (positionAnalyse: PositionAnalyticsState) => {
    const { positionAnalytics } = positionAnalyse ?? {};
    const { result_pnls: resultPnls = [] } = positionAnalytics?.data ?? {};
    const resultSpotPrices: number[] =
      resultPnls?.map(resultPnl => Math.trunc(Number(resultPnl.spot_price))) ?? [];
    return resultSpotPrices;
  }
);

const positionAnalyseExpirySpotPricesSelector = createSelector(
  [positionAnalyseSelector],
  (positionAnalyse: PositionAnalyticsState) => {
    return pipe(
      map((expiryPnl: PNLData) => parseInt(expiryPnl.spot_price, 10).toString()),
      uniq,
      sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    )(positionAnalyse?.positionAnalytics?.data?.expiry_pnls || []);
  }
);

const positionAnalyseProductTickerPairListSelector = createSelector(
  [
    selectAllOptionsChainContractsTickers,
    productsSelector,
    (_, activeAsset) => activeAsset,
  ],
  (allOptionsTicker, products, activeAsset) => {
    return allOptionsTicker
      .filter(ticker => Number.isSafeInteger(ticker.product_id))
      .map(ticker => {
        return { ticker, product: products[ticker.product_id!] };
      })
      .filter(contractData => {
        const { product } = contractData;
        const isNotFutures = !isFutures(product.contract_type);

        const settlementDate = isNotFutures
          ? dayjs(toUTC(product.settlement_time as Date))
          : null;
        const checkDateUTC = basketAnalyzeGetCheckDateUTC();

        return (
          product.underlying_asset?.symbol === activeAsset &&
          (!isNotFutures || settlementDate?.isSame(checkDateUTC, 'day'))
        );
      });
  }
);

const positionAnalyseExpirySpotPriceMaxSelector = createSelector(
  [positionAnalyseExpirySpotPricesSelector],
  expirySpotPrices => {
    return pipe(map(Number), reduce<number, number>(max, -Infinity))(expirySpotPrices);
  }
);

const positionAnalyseExpirySpotPriceMinSelector = createSelector(
  [positionAnalyseExpirySpotPricesSelector],
  expirySpotPrices => {
    return pipe(map(Number), reduce<number, number>(min, Infinity))(expirySpotPrices);
  }
);

const positionAnalyseResultSpotPriceLabelsMaxSelector = createSelector(
  [positionAnalyseResultSpotPricesSelector],
  positionAnalyseResultSpotPrices => {
    return pipe(
      map(Number),
      reduce<number, number>(max, -Infinity)
    )(positionAnalyseResultSpotPrices);
  }
);

const positionAnalyseResultSpotPriceLabelsMinSelector = createSelector(
  [positionAnalyseResultSpotPricesSelector],
  positionAnalyseResultSpotPrices => {
    return pipe(
      map(Number),
      reduce<number, number>(min, Infinity)
    )(positionAnalyseResultSpotPrices);
  }
);

const positionAnalyseOpenInterestCategoriesSelector = createSelector(
  [positionAnalyseProductTickerPairListSelector],
  productTickerPairList => {
    return sort(
      (a, b) => a - b,
      uniq(
        pluck(
          'strike_price',
          map(productTickerPair => productTickerPair.product, productTickerPairList)
        )
      )
    );
  }
);

const positionAnalyseGroupOiByStrikePricesCoordinatesSelector = createSelector(
  [positionAnalyseProductTickerPairListSelector],
  productTickerPairList => {
    const [callOptionsContracts, putOptionsContracts] = partition(
      ({ product }) => product.contract_type === ContractType.CallOptions,
      productTickerPairList
    );

    const getOiByStrikePrice = pipe<
      [typeof productTickerPairList],
      { [strike: string]: typeof productTickerPairList },
      { [strikePrice: string]: number }
    >(
      groupBy(({ product }) => product.strike_price ?? '0'),
      mapObjIndexed(
        pipe(
          map(({ ticker }) => {
            const productId = String(ticker.product_id);
            return Number(oiSelectorById(productId));
          }),
          sum
        )
      )
    );

    const callOiByStrikePrices = getOiByStrikePrice(callOptionsContracts);
    const putOiByStrikePrices = getOiByStrikePrice(putOptionsContracts);

    const callsStrikePrices =
      Object.keys(callOiByStrikePrices)?.map(callOiByStrikePrice => {
        return {
          x: callOiByStrikePrice,
          y: callOiByStrikePrices[callOiByStrikePrice],
        };
      }) ?? [];

    const putsStrikePrices =
      Object.keys(putOiByStrikePrices)?.map(putOiByStrikePrice => {
        return {
          x: putOiByStrikePrice,
          y: putOiByStrikePrices[putOiByStrikePrice],
        };
      }) ?? [];

    return [callsStrikePrices, putsStrikePrices];
  }
);

const positionAnalyseExpiryPnlMaxSelector = createSelector(
  [positionAnalyseSelector],
  positionAnalyse => {
    return pipe(
      map((expiryPnl: PNLData) => parseInt(expiryPnl.pnl, 10)),
      map(Math.abs),
      reduce<number, number>(max, -Infinity)
    )(positionAnalyse?.positionAnalytics?.data?.expiry_pnls ?? []);
  }
);

const positionAnalyseOpenInterestMaxSelector = createSelector(
  [positionAnalyseProductTickerPairListSelector],
  productTickerPairList => {
    return pipe(
      map(productTickerPair => {
        const selectedProductOIValue = oiSelectorById(
          String(productTickerPair.product.id)
        );
        return Number(selectedProductOIValue);
      }),
      map(Math.abs),
      reduce<number, number>(max, -Infinity)
    )(productTickerPairList);
  }
);

const positionAnalyseExpiryPnlCoordinatesSelector = createSelector(
  [positionAnalyseExpirySpotPricesSelector, positionAnalyseSelector],
  (expirySpotPrices, positionAnalyse) => {
    return expirySpotPrices.map(expirySpotPrice => {
      const selectedPnl =
        positionAnalyse?.positionAnalytics?.data?.expiry_pnls?.find(
          expiryPnl => parseInt(expiryPnl.spot_price, 10) === Number(expirySpotPrice)
        )?.pnl ?? 0;
      return {
        x: Number(expirySpotPrice),
        y: Number(selectedPnl),
      };
    });
  }
);

const positionAnalyseTargetDatePnlCoordinatesSelector = createSelector(
  [
    positionAnalyseExpirySpotPricesSelector,
    positionAnalyseSelector,
    (_, defaultTargetPrice) => defaultTargetPrice,
  ],
  (expirySpotPrices, positionAnalyse, defaultTargetPrice) => {
    return expirySpotPrices.map(expirySpotPrice => {
      const selectedTargetPriceOfResulPnl =
        positionAnalyse?.positionAnalytics?.data?.result_pnls?.find(resultPnl => {
          return (
            Math.floor(parseInt(resultPnl?.spot_price, 10)) ===
            Math.floor(positionAnalyse?.pnlTab?.selectedTargetPrice)
          );
        });
      const selectedResultPnl =
        positionAnalyse?.positionAnalytics?.data?.result_pnls?.find(
          resultPnl => parseInt(resultPnl?.spot_price, 10) === Number(expirySpotPrice)
        );
      const selectedExpiryPnl =
        positionAnalyse?.positionAnalytics?.data?.expiry_pnls?.find(
          expiryPnl => parseInt(expiryPnl?.spot_price, 10) === Number(expirySpotPrice)
        );
      return {
        x: Number(expirySpotPrice),
        y: Number(selectedResultPnl?.pnl),
        resultPnl: selectedResultPnl?.pnl,
        expiryPnl: selectedExpiryPnl?.pnl,
        resultPnlFormatted: numberCommaSeparator(
          Math.floor(Number(selectedResultPnl?.pnl))
        ),
        expiryPnlFormatted: numberCommaSeparator(
          Math.floor(Number(selectedExpiryPnl?.pnl))
        ),

        selectedExpiryDate: dateFormat(
          positionAnalyse?.pnlTab?.selectedTargetDate,
          TIME_FORMATS.ddd_DD_MMM
        ),
        settlingAsset: VANILLA_SETTLING_ASSET,
        profit: Number(selectedTargetPriceOfResulPnl?.pnl),
        formattedProfit: numberCommaSeparator(
          Number(selectedTargetPriceOfResulPnl?.pnl ?? 0).toFixed(2)
        ),
        profitPercentage: Number(
          pipe(
            subtract(positionAnalyse?.pnlTab?.selectedTargetPrice),
            flip(divide)(defaultTargetPrice),
            multiply(100)
          )(defaultTargetPrice).toFixed(1)
        ),
      };
    });
  }
);

export {
  positionAnalyseActiveOrders,
  positionAnalyseExpiryPnlCoordinatesSelector,
  positionAnalyseExpiryPnlMaxSelector,
  positionAnalyseResultSpotPriceLabelsMaxSelector,
  positionAnalyseResultSpotPriceLabelsMinSelector,
  positionAnalyseExpirySpotPriceMaxSelector,
  positionAnalyseExpirySpotPriceMinSelector,
  positionAnalyseExpirySpotPricesSelector,
  positionAnalyseGreeksAndPnLDataSelector,
  positionAnalyseGroupOiByStrikePricesCoordinatesSelector,
  positionAnalyseOpenInterestCategoriesSelector,
  positionAnalyseOpenInterestMaxSelector,
  positionAnalyseOrdersSelector,
  positionAnalysePnlDataSelector,
  positionAnalyseProductTickerPairListSelector,
  positionAnalyseResultSpotPricesSelector,
  positionAnalyseSelector,
  positionAnalyseTargetDatePnlCoordinatesSelector,
  positionAnalyseTargetDatesIndexMapSelector,
  positionAnalyseTargetDatesSelector,
};
